import { useEffect, useState, useMemo } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ShieldCheck, Crown, Search, Loader2, Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";
import PermissionsEditorDialog from "@/components/PermissionsEditorDialog";

const LEVEL_RANK = { none: 0, read: 1, write: 2, admin: 3 };
const LEVEL_BADGE = {
  none:  { label: "—", className: "bg-zinc-100 text-zinc-400 border-zinc-200" },
  read:  { label: "R", className: "bg-blue-50 text-blue-700 border-blue-200" },
  write: { label: "W", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  admin: { label: "A", className: "bg-amber-50 text-amber-800 border-amber-200" },
};

const MODULE_LABELS = {
  dashboard: "Tableau de bord",
  orders: "Commandes",
  clients: "Clients",
  employees: "Salariés",
  sellers: "Vendeurs",
  subcontractors: "Sous-traitants",
  suppliers: "Fournisseurs",
  equipment: "Matériel",
  analytics: "Analyses",
  commissions: "Commissions",
  finance: "Finance",
  admin_tasks: "Administratif",
  field_requests: "Demandes chantier",
  employee_submissions: "Fiches salariés",
  settings: "Paramètres",
  billing: "Facturation",
};

/**
 * PermissionsAdminPage — matrice cliquable pour le gestionnaire d'organisation.
 *
 * Vue d'ensemble : pour chaque user de l'org, on affiche en colonnes les
 * 15+ modules, avec un badge compact (—/R/W/A). Le clic ouvre l'éditeur fin.
 *
 * Accessible via /administration/permissions (entrée admin de l'org).
 */
const PermissionsAdminPage = () => {
  const { activeOrgId, isOrgAdmin, isSuperAdmin, permissionModules, refresh } = useOrg();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [templates, setTemplates] = useState([]);

  const allowed = isOrgAdmin || isSuperAdmin;

  const fetchMembers = async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const res = await api.getOrgMembers(activeOrgId);
      const list = Array.isArray(res.data) ? res.data : (res.data?.members || []);
      setMembers(list);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    fetchMembers();
    api.getRoleTemplates?.()
      .then(r => setTemplates(r.data?.templates || []))
      .catch(() => setTemplates([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrgId, allowed]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter(m => (m.email || "").toLowerCase().includes(q) || (m.name || "").toLowerCase().includes(q));
  }, [members, search]);

  const sortedModules = permissionModules?.length ? permissionModules : Object.keys(MODULE_LABELS);

  if (!allowed) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground text-sm">
          Cette page est réservée aux administrateurs de l'organisation.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="permissions-admin-page">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 pb-3">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Matrice des permissions
              <Badge variant="outline" className="ml-1">{filtered.length}{filtered.length !== members.length ? ` / ${members.length}` : ""}</Badge>
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Aperçu rapide des droits par utilisateur. Clique sur une ligne pour les modifier en détail.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <Input
                placeholder="Rechercher un utilisateur..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-72 pl-8"
                data-testid="permissions-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Aucun utilisateur trouvé.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky left-0 bg-white z-10">Utilisateur</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Template</TableHead>
                  {sortedModules.map(m => (
                    <TableHead key={m} className="text-center text-[10px] uppercase tracking-tight" title={MODULE_LABELS[m] || m}>
                      {(MODULE_LABELS[m] || m).slice(0, 8)}
                    </TableHead>
                  ))}
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((member) => {
                  const isAdmin = member.org_role === "admin";
                  const perms = isAdmin
                    ? sortedModules.reduce((acc, m) => ({ ...acc, [m]: "admin" }), {})
                    : (member.permissions || {});
                  const tmpl = templates.find(t => t.id === member.role_template);
                  return (
                    <TableRow
                      key={member.user_id}
                      className="hover:bg-zinc-50/60 cursor-pointer"
                      onClick={() => setEditTarget(member)}
                      data-testid={`perm-row-user-${member.user_id}`}
                    >
                      <TableCell className="sticky left-0 bg-white z-10 font-medium">
                        <div className="flex flex-col">
                          <span>{member.name || member.email}</span>
                          {member.name && <span className="text-[11px] text-zinc-400">{member.email}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isAdmin ? (
                          <Badge className="bg-amber-50 text-amber-800 border-amber-200 border"><Crown className="h-3 w-3 mr-1" />Admin org</Badge>
                        ) : (
                          <Badge variant="outline">Membre</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {tmpl ? (
                          <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 border">
                            <Sparkles className="h-3 w-3 mr-1" />{tmpl.label || tmpl.id}
                          </Badge>
                        ) : (
                          <span className="text-zinc-400 text-xs">—</span>
                        )}
                      </TableCell>
                      {sortedModules.map((m) => {
                        const lvl = perms[m] || "none";
                        const b = LEVEL_BADGE[lvl] || LEVEL_BADGE.none;
                        return (
                          <TableCell key={m} className="text-center px-1">
                            <span
                              className={`inline-flex items-center justify-center w-6 h-6 rounded-md text-[10px] font-bold border ${b.className}`}
                              title={`${MODULE_LABELS[m] || m} : ${lvl}`}
                            >
                              {b.label}
                            </span>
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setEditTarget(member); }} data-testid={`edit-perms-${member.user_id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap gap-4 text-xs items-center">
            <span className="font-semibold text-muted-foreground">Légende :</span>
            {Object.entries(LEVEL_BADGE).map(([k, b]) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-md text-[10px] font-bold border ${b.className}`}>{b.label}</span>
                {k === "none" ? "Aucun accès" : k === "read" ? "Lecture" : k === "write" ? "Écriture" : "Admin module"}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      <PermissionsEditorDialog
        open={!!editTarget}
        member={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { fetchMembers(); refresh(); }}
      />
    </div>
  );
};

export default PermissionsAdminPage;
